/**
 * src/auth/roleClaims.js — single source of truth for the caller's authorization
 * roles on the frontend (Etap 9 Faza 0).
 *
 * Authentication still comes from Firebase (the ID token), but roles now live in
 * Postgres and are served by GET /api/auth/me. Both useUser.js and the router
 * guard consult this module so there is exactly one place that fetches /me and
 * caches the result — keeping the previous `{admin, konstruktor, sprzedawca,
 * produkcja}` boolean shape the rest of the app already expects.
 *
 * Caching: per-uid, with in-flight dedup so a burst of guarded navigations after
 * login fires a single request. On a transient fetch failure we reuse the last
 * good value for the same uid (navigation shouldn't hard-fail on a blip); with no
 * cached value we return all-false, which makes the guard deny — fail closed.
 */

import { fetchMe } from '@/sync/adminApi';

const EMPTY = Object.freeze({ admin: false, konstruktor: false, sprzedawca: false, produkcja: false });

let cache = null; // { uid, claims }
let inFlight = null; // { uid, promise }

function normalize(meClaims) {
  return {
    admin: meClaims?.admin === true,
    konstruktor: meClaims?.konstruktor === true,
    sprzedawca: meClaims?.sprzedawca === true,
    produkcja: meClaims?.produkcja === true,
  };
}

/**
 * Resolve the role claims for the given Firebase user. Returns the EMPTY set when
 * `user` is null. Caches per uid; dedups concurrent calls.
 * @param {{uid: string}|null|undefined} user
 * @returns {Promise<{admin:boolean,konstruktor:boolean,sprzedawca:boolean,produkcja:boolean}>}
 */
export async function getRoleClaims(user) {
  if (!user || !user.uid) return EMPTY;
  const uid = user.uid;

  if (cache && cache.uid === uid) return cache.claims;
  if (inFlight && inFlight.uid === uid) return inFlight.promise;

  const promise = (async () => {
    try {
      const me = await fetchMe();
      const claims = normalize(me?.claims);
      cache = { uid, claims };
      return claims;
    } catch (err) {
      // Reuse last good value for this uid if we have one; otherwise fail closed.
      if (cache && cache.uid === uid) return cache.claims;
      console.error('roleClaims: /api/auth/me failed, denying by default', err);
      return EMPTY;
    } finally {
      inFlight = null;
    }
  })();

  inFlight = { uid, promise };
  return promise;
}

/** Clear the cached claims (call on logout / auth-state → null). */
export function clearRoleClaims() {
  cache = null;
  inFlight = null;
}
